import { useMemo } from 'react';
import { Badge } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import DppModal from './DppModal';
import getLocalizedText from '../Utils';
import Config from '../../config/Config';

const DetailItem = ({ label, value, code = false }) => (
  <div className="mb-2">
    <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '2px' }}>
      {label}
    </div>
    {code ? (
      <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '5px 10px', marginTop: '2px' }}>
        <code style={{ fontSize: '12px', color: '#555', wordBreak: 'break-all' }}>{value}</code>
      </div>
    ) : (
      <div style={{ fontSize: '14px', color: '#333', wordBreak: 'break-word' }}>{value}</div>
    )}
  </div>
);

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px', marginBottom: '10px' }}>
    {children}
  </div>
);

const SubmodalDetailsModal = ({ show, handleClose, submodel }) => {
  const intl = useIntl();
  const { mainColor } = Config.appearance;

  const {
    idShort,
    modelType = 'Submodel',
    kind,
    id,
    description,
    displayName,
    embeddedDataSpecifications = [],
    supplementalSemanticIds = [],
    semanticId,
  } = submodel || {};

  const displayNameToShow = getLocalizedText(displayName, null, intl.locale) || idShort || 'Unnamed Submodel';

  const semanticIdValue = useMemo(() => {
    if (!semanticId || semanticId.type !== 'ModelReference') return null;
    return semanticId.keys?.[0]?.value || null;
  }, [semanticId]);

  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });

  return (
    <DppModal show={show} handleClose={handleClose} size="lg" centered title={displayNameToShow}>
      <div>

        {/* Basic info grid */}
        <div className="row g-0 mb-1">
          {idShort && (
            <div className="col-12 col-sm-6 mb-2 pe-sm-3">
              <DetailItem label={t('id_short', 'ID Short')} value={idShort} />
            </div>
          )}
          <div className="col-12 col-sm-6 mb-2">
            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '5px' }}>
              {t('model_type', 'Model Type')}
            </div>
            <Badge bg="" style={{ backgroundColor: mainColor, fontSize: '12px', fontWeight: 500 }}>
              {modelType}
            </Badge>
          </div>
          {kind && (
            <div className="col-12 col-sm-6 mb-2 pe-sm-3">
              <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '5px' }}>
                {t('kind', 'Kind')}
              </div>
              <Badge bg="light" text="dark" style={{ fontSize: '12px', fontWeight: 500, border: '1px solid #dee2e6' }}>
                {kind}
              </Badge>
            </div>
          )}
        </div>

        {id && <DetailItem label={t('submodel_id', 'Submodel ID')} value={id} code />}

        {getLocalizedText(description, null, intl.locale) && (
          <DetailItem label={t('description', 'Description')} value={getLocalizedText(description, '—', intl.locale)} />
        )}

        {/* Semantic ID */}
        {semanticIdValue && (
          <>
            <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
            <DetailItem label={t('semantic_id', 'Semantic ID')} value={semanticIdValue} code />
          </>
        )}

        {/* Supplemental Semantic IDs */}
        {supplementalSemanticIds.length > 0 && (
          <>
            <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
            <SectionLabel>{t('supplemental_semantic_ids', 'Supplemental Semantic IDs')}</SectionLabel>
            <div className="d-flex flex-column gap-2">
              {supplementalSemanticIds.map((sup, i) => (
                <div key={i} style={{ background: '#f8f9fa', padding: '6px 10px', borderRadius: '8px' }}>
                  <code style={{ fontSize: '12px', color: '#555', wordBreak: 'break-all' }}>
                    {sup.keys?.[0]?.value || 'Invalid reference'}
                  </code>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Data Specifications */}
        {embeddedDataSpecifications.length > 0 && (
          <>
            <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
            <SectionLabel>{t('data_specifications', 'Data Specifications')}</SectionLabel>
            <div className="d-flex flex-column gap-2">
              {embeddedDataSpecifications.map((ds, i) => {
                const nameEn = ds.dataSpecificationContent?.preferredName?.find(p => p.language === 'en')?.text;
                const nameDe = ds.dataSpecificationContent?.preferredName?.find(p => p.language === 'de')?.text;
                const fallback = ds.dataSpecificationContent?.preferredName?.[0]?.text;
                const name = nameEn || nameDe || fallback || 'Unknown';
                return (
                  <div key={i} style={{ background: '#f8f9fa', padding: '8px 12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>{name}</div>
                    {nameEn && nameDe && nameEn !== nameDe && (
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{nameDe}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

      </div>
    </DppModal>
  );
};

export default SubmodalDetailsModal;
